import { ModalContext } from 'app/app';
import axios from 'axios';
import { IconedButton } from 'components/IconedButton';
import { Pagination } from 'components/Pagination';
import {
  Animate,
  Grid,
  Loader,
  Search,
  Table,
  ViewContext,
} from 'components/lib';
import DateUtils from 'helpers/dateUtils';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './styles.module.scss';

export function Commissions() {
  const { id } = useParams();
  const [payload, setPayload] = useState();
  const [agencyData, setAgencyData] = useState({});
  const [commissions, setCommissions] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQueryString, setSearchQueryString] = useState('');

  const { setModal } = useContext(ModalContext);
  const context = useContext(ViewContext);
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

  async function listCommissions(page, firstTime) {
    setLoading(true);

    if (firstTime) {
      setPageLoading(true);

      await axios
        .get(`/api/account/${id}`)
        .then(async (res) => {
          setAgencyData(res.data.data);
        })
        .catch((err) => {
          console.log(err);

          context.notification.show(
            'There was an error loading the agency data',
            'error',
            true,
            'toast',
            'x-circle'
          );
        });
    }

    await axios
      .get(
        `/api/commission?page=${page}&search=${searchQueryString}&account_id=${id}`
      )
      .then(async (res) => {
        setPayload(res.data.data);

        const helper = new DateUtils();
        const timezone = await helper.getTimezone();

        const formattedValues = res.data.data.data.map((document) => {
          return {
            id: document.id,
            name: document.name,
            uploaded_by: document.uploaded_by,
            type: document.type,
            link: document.s3_link,
            'Created At': helper.timestamp(
              document.createdAt,
              timezone
            ),
          };
        });

        setCommissions(formattedValues);
      })
      .catch((err) => {
        console.log(err);

        context.notification.show(
          'There was an error while fetching commissions data. Please try again later.',
          'error',
          false,
          'toast',
          'x-circle'
        );
      });

    setPageLoading(false);
    setLoading(false);
  }

  useEffect(() => {
    listCommissions(page, true);
  }, []);

  return (
    <>
      {pageLoading ? (
        <Loader />
      ) : (
        <Animate type="pop">
          <div
            className={styles.agencyOverview}
            style={{ marginBottom: 20 }}
          >
            <div className={styles.left}>
              <div>
                <div className={styles.rowInfo}>
                  <div className={styles.info}>
                    <span>Company name:</span>
                    <span>
                      {agencyData.name ? agencyData.name : ''}
                    </span>
                  </div>
                </div>
                <div className={styles.rowInfo}>
                  <div className={styles.info}>
                    <span>DBA Name:</span>
                    <span>
                      {agencyData.dba_name
                        ? agencyData.dba_name
                        : 'Not found'}
                    </span>
                  </div>
                </div>
                <div className={styles.rowInfo}>
                  <div className={styles.info}>
                    <span>Owner name:</span>
                    <span>
                      {agencyData.owner_name
                        ? agencyData.owner_name
                        : ''}
                    </span>
                  </div>
                </div>
                <div className={styles.rowInfo}>
                  <div className={styles.info}>
                    <span>Email:</span>
                    <span>
                      {agencyData.owner_email
                        ? agencyData.owner_email
                        : ''}
                    </span>
                  </div>
                </div>
                <div className={styles.rowInfo}>
                  <div className={styles.info}>
                    <span>Address line 1:</span>
                    <span>
                      {agencyData.address1 ? agencyData.address1 : ''}
                    </span>
                  </div>
                </div>
                <div className={styles.rowInfo}>
                  <div className={styles.info}>
                    <span>Address line 2:</span>
                    <span>
                      {agencyData.address2 ? agencyData.address2 : ''}
                    </span>
                  </div>
                </div>
                <div className={styles.rowInfo}>
                  <div className={styles.info}>
                    <span>Phone:</span>
                    <span>
                      {agencyData.certificate_contacts &&
                        agencyData.certificate_contacts[0] &&
                        agencyData.certificate_contacts[0].phone
                        ? agencyData.certificate_contacts[0].phone
                        : 'Not found'}
                    </span>
                  </div>
                </div>
                <div className={styles.rowInfo}>
                  <div className={styles.info}>
                    <span>City:</span>
                    <span>
                      {agencyData.city ? agencyData.city : ''}
                    </span>
                  </div>
                  <div className={styles.info}>
                    <span>State:</span>
                    <span>
                      {agencyData.state ? agencyData.state : ''}
                    </span>
                  </div>
                  <div className={styles.info}>
                    <span>ZIP:</span>
                    <span>
                      {agencyData.zip ? agencyData.zip : ''}
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ marginLeft: 50 }}>
                <div className={styles.rowInfo}>
                  <div className={styles.info}>
                    <span>Active Policies:</span>
                    <span>
                      {agencyData.active_policies
                        ? agencyData.active_policies
                        : 0}
                    </span>
                  </div>
                </div>
                <div className={styles.rowInfo}>
                  <div className={styles.info}>
                    <span>Active Premium:</span>
                    <span>
                      {agencyData.active_premium
                        ? formatter.format(agencyData.active_premium)
                        : 0}
                    </span>
                  </div>
                </div>
                <div className={styles.rowInfo}>
                  <div className={styles.info}>
                    <span>Pending Cancellations:</span>
                    <span>
                      {agencyData.pending_cancellations
                        ? agencyData.pending_cancellations
                        : 0}
                    </span>
                  </div>
                </div>
                <div className={styles.rowInfo}>
                  <div className={styles.info}>
                    <span>Average Quotes per Month:</span>
                    <span>
                      {agencyData.average_quotes_per_month
                        ? agencyData.average_quotes_per_month
                        : 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.right}>
              <IconedButton
                size="150px"
                color="#d32425"
                textColor="white"
                icon="log-out"
                text="Impersonate"
                marginRight
                action={async () => {
                  setModal({
                    name: 'confirmation',
                    data: {
                      title: `Impersonate agency's owner`,
                      description: `Do you want to impersonate this agency's owner?`,
                    },
                    action: async () => {
                      setModal({});

                      axios
                        .post('/api/impersonate', {
                          impersonatableId: id,
                          type: 'account',
                        })
                        .then((res) => {
                          //send message
                          var receiver =
                            document.getElementById(
                              'receiver'
                            ).contentWindow;

                          receiver.postMessage(
                            res.data,
                            `${window.location.host ===
                              'localhost:5002'
                              ? 'http://localhost:3000'
                              : 'https://app.coverbadger.com'
                            }`
                          );

                          window.location.href = `${window.location.host === 'localhost:5002'
                              ? 'http://localhost:3000'
                              : 'https://app.coverbadger.com'
                            }/app/dashboard`;
                        })
                        .catch((err) => {
                          console.log(err);
                          setModal('');
                        });
                      setModal('');
                    },
                  });
                }}
              />
              <IconedButton
                size="170px"
                color="#d32425"
                textColor="white"
                icon="refresh-ccw"
                text="Re-send invite"
                marginRight
                action={async () => {
                  await axios
                    .post('/api/invite', {
                      email: agencyData.owner_email,
                      agency_name: agencyData.name,
                      owner_name:
                        agencyData.owner_name.split(' ')[0] +
                        ' ' +
                        agencyData.owner_name.split(' ')[1],
                      type: 'agency',
                    })
                    .catch((error) => {
                      console.log(error);

                      context.notification.show(
                        'Failed to send the invite again.',
                        'failed',
                        true,
                        'toast',
                        'x-circle'
                      );
                    });

                  setModal({
                    name: 'success',
                    data: {
                      title: `Email sent!`,
                      description: `Email successfully sended to agency owner.`,
                    },
                    action: async () => { },
                  });
                }}
              />
              <IconedButton
                size="150px"
                color="#d32425"
                textColor="white"
                icon="airplay"
                text="Pull Policy"
                marginRight
                action={async () => {
                  setModal({
                    name: 'pull_policy_modal',
                    data: {
                      agency_id: id,
                    },
                  });
                }}
              />
              {agencyData.submissions_masked === true ? (
                <IconedButton
                  size="230px"
                  color="#2ca312"
                  textColor="white"
                  icon="shield-off"
                  text="Unmask Submissions"
                  marginRight
                  action={() => {
                    setModal({
                      name: 'confirmation',
                      data: {
                        title: `Unmask agency's Submissions`,
                        description: `Do you want to unmask the submissions of this agency?`,
                      },
                      action: async () => {
                        setModal({});

                        await axios
                          .patch('/api/account/' + agencyData.id, {
                            submissions_masked: false,
                          })
                          .then(() => {
                            window.location.reload();
                          })
                          .catch((error) => {
                            console.log(error);
                            setModal({});

                            context.notification.show(
                              'Failed to send the invite again.',
                              'failed',
                              true,
                              'toast',
                              'x-circle'
                            );
                          });
                      },
                    });
                  }}
                />
              ) : (
                <IconedButton
                  size="190px"
                  color="#000"
                  textColor="white"
                  icon="shield"
                  text="Mask Submissions"
                  marginRight
                  action={() => {
                    setModal({
                      name: 'confirmation',
                      data: {
                        title: `Mask agency's Submissions`,
                        description: `Do you want to mask the submissions of this agency?`,
                      },
                      action: async () => {
                        setModal({});

                        await axios
                          .patch('/api/account/' + agencyData.id, {
                            submissions_masked: true,
                          })
                          .then(() => {
                            window.location.reload();
                          })
                          .catch((error) => {
                            console.log(error);
                            setModal({});

                            context.notification.show(
                              'Failed to send the invite again.',
                              'failed',
                              true,
                              'toast',
                              'x-circle'
                            );
                          });
                      },
                    });
                  }}
                />
              )}
              {agencyData.status === 'partial_disabled' ||
                agencyData.status === 'total_disabled' ? (
                <IconedButton
                  size="170px"
                  color="#2ca312"
                  textColor="#fff"
                  icon="x-octagon"
                  text="Activate Agency"
                  action={() => {
                    setModal({
                      name: 'confirmation',
                      data: {
                        title: `Activate Agency`,
                        description: `Do you want to activate this agency?`,
                      },
                      action: async () => {
                        setModal({});

                        await axios
                          .patch('/api/account/' + agencyData.id, {
                            status: 'none',
                          })
                          .then(() => {
                            window.location.reload();
                          })
                          .catch((error) => {
                            console.log(error);
                            setModal({});

                            context.notification.show(
                              'Failed to send the invite again.',
                              'failed',
                              true,
                              'toast',
                              'x-circle'
                            );
                          });
                      },
                    });
                  }}
                />
              ) : (
                <IconedButton
                  size="170px"
                  color="#000"
                  textColor="white"
                  icon="x-octagon"
                  text="Disable Agency"
                  action={() => {
                    setModal({
                      name: 'confirmation',
                      data: {
                        title: `Disable agency`,
                        description: `Do you want to disable this agency?`,
                      },
                      action: async () => {
                        setModal({});

                        await axios
                          .post(
                            '/api/account/disable/' + agencyData.id
                          )
                          .then(() => {
                            window.location.reload();
                          })
                          .catch((error) => {
                            console.log(error);
                            setModal({});

                            context.notification.show(
                              'Failed to send the invite again.',
                              'failed',
                              true,
                              'toast',
                              'x-circle'
                            );
                          });
                      },
                    });
                  }}
                />
              )}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignContent: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex' }}>
              <div
                onClick={() => {
                  window.location.href = '/admin/agency/' + id;
                }}
                style={{
                  backgroundColor: '#EAEAEA',
                  width: '150px',
                  color: '#090E3480',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 10,
                  fontSize: 16,
                  fontWeight: 600,
                  borderTopLeftRadius: 10,
                  borderTopRightRadius: 10,
                  cursor: 'pointer',
                }}
              >
                Users
              </div>
              <div
                onClick={() => {
                  window.location.href =
                    '/admin/agency/' + id + '/notes';
                }}
                style={{
                  backgroundColor: '#EAEAEA',
                  width: '150px',
                  color: '#090E3480',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 10,
                  fontSize: 16,
                  fontWeight: 600,
                  borderTopLeftRadius: 10,
                  borderTopRightRadius: 10,
                  cursor: 'pointer',
                }}
              >
                Notes
              </div>
              <div
                onClick={() => {
                  window.location.href =
                    '/admin/agency/' + id + '/documents';
                }}
                style={{
                  backgroundColor: '#EAEAEA',
                  width: '150px',
                  color: '#090E3480',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 10,
                  fontSize: 16,
                  fontWeight: 600,
                  borderTopLeftRadius: 10,
                  borderTopRightRadius: 10,
                  cursor: 'pointer',
                }}
              >
                Documents
              </div>
              <div
                style={{
                  width: '150px',
                  backgroundColor: 'black',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 10,
                  fontSize: 16,
                  fontWeight: 600,
                  borderTopLeftRadius: 10,
                  borderTopRightRadius: 10,
                  cursor: 'default',
                }}
              >
                Commissions
              </div>
            </div>
            <IconedButton
              size="190px"
              color="#d32425"
              textColor="white"
              icon="plus-circle"
              text="Add Commission"
              action={() => {
                setModal({
                  name: 'commission_modal',
                  agency_id: id,
                  action: () => {
                    listCommissions(page, searchQueryString, false);
                  },
                });
              }}
            />
          </div>
          {window.location.host === 'localhost:5002' ? (
            <iframe
              id="receiver"
              src={`http://localhost:3000`}
              width="500"
              height="200"
              style={{ display: 'none' }}
            />
          ) : (
            <iframe
              id="receiver"
              src={`https://app.coverbadger.com`}
              width="500"
              height="200"
              style={{ display: 'none' }}
            />
          )}
          <Grid cols="1">
            <div className={styles.wrapper}>
              <div style={{ margin: 20 }}>
                <div style={{ marginBottom: 30 }}>
                  <Search
                    callback={(query) => {
                      setSearchQueryString(query);
                    }}
                  />
                </div>
                <Table
                  loading={loading}
                  data={commissions}
                  badge={{
                    col: 'status',
                    condition: [
                      { value: 'Active', color: 'green' },
                      { value: 'Inactive', color: 'red' },
                    ],
                  }}
                  show={['name', 'uploaded_by', 'Created At']}
                  actions={{
                    custom: [
                      {
                        icon: 'download',
                        action: (row) => {
                          window.open(row.link);
                        },
                        color: '#637381',
                        tooltip: 'Download commission',
                      },
                      {
                        icon: 'trash',
                        action: (doc) => {
                          setModal({
                            name: 'confirmation',
                            data: {
                              title: `Delete commission`,
                              description: `Do you really want to delete this quote"?`,
                            },
                            action: async () => {
                              setModal('');
                              setLoading(true);

                              await axios
                                .delete(`/api/commission/${doc.id}`)
                                .then(() => {
                                  listCommissions(
                                    page,
                                    searchQueryString,
                                    false
                                  );
                                })
                                .catch(() => {
                                  setLoading(false);

                                  context.notification.show(
                                    'Failed to load table data',
                                    'error',
                                    true,
                                    'toast',
                                    'x-circle'
                                  );
                                });
                            },
                          });
                        },
                        color: '#637381',
                        tooltip: 'Delete commission',
                      },
                    ],
                  }}
                />
                {!loading && payload && (
                  <Pagination
                    pagesCount={payload.pages_count}
                    active={payload.page}
                    onChange={async (newPage) => {
                      setPage(newPage);
                      listCommissions(newPage, searchQueryString, false);
                    }}
                  />
                )}
              </div>
            </div>
          </Grid>
        </Animate>
      )}
    </>
  );
}